/**
 * Primitivas de validación reutilizables.
 *
 * La regla que gobierna este fichero: **el dinero es un string decimal de principio a fin.**
 * Un `z.number()` para dinero convierte "1234.56" en un IEEE-754 y a partir de ahí cualquier
 * suma introduce el error que el esquema de la base evita a propósito con `numeric(14,2)`.
 */
import { z } from 'zod';

/**
 * Importe decimal. Acepta con y sin decimales y **normaliza a dos**, porque un formulario
 * emite tanto `"1200"` como `"1200.5"` y la base espera `numeric(14,2)`.
 *
 * DESVIACIÓN respecto al enunciado: allí el patrón era `/^-?\d+(\.\d{1,2})$/`, que hace los
 * decimales OBLIGATORIOS y rechazaría `"1200"` — el caso más común al teclear una renta.
 * Se hacen opcionales y se rellenan.
 */
export const zMoney = z
  .string()
  .trim()
  .regex(/^-?\d{1,12}(\.\d{1,2})?$/, 'Importe inválido. Usa hasta 2 decimales, con punto.')
  .transform((value) => {
    const [whole, fraction = ''] = value.split('.');
    return `${whole}.${fraction.padEnd(2, '0')}`;
  });

/** Importe que no puede ser negativo (una renta, una fianza, una prima). */
export const zMoneyPositive = zMoney.refine(
  (value) => !value.startsWith('-'),
  'El importe no puede ser negativo.',
);

/** Importe estrictamente mayor que cero (una renta de 0 € no es un contrato). */
export const zMoneyNonZero = zMoneyPositive.refine(
  (value) => Number(value) > 0,
  'El importe debe ser mayor que cero.',
);

export const zUuid = z.string().uuid('Identificador inválido.');

/** Fecha de negocio `YYYY-MM-DD`. Se valida que exista de verdad: `2025-02-30` no cuela. */
export const zIsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha debe tener el formato AAAA-MM-DD.')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'Esa fecha no existe en el calendario.');

/** Texto obligatorio ya recortado. `.trim()` antes de `.min()`: "   " no es un nombre. */
export const zText = (max: number, label = 'Este campo') =>
  z.string().trim().min(1, `${label} es obligatorio.`).max(max, `Máximo ${max} caracteres.`);

/**
 * Texto opcional que normaliza la cadena vacía a `null`. Un `<input>` vacío envía `""`, y
 * guardarlo como `""` en vez de `NULL` rompe los `IS NULL` de todas las consultas.
 */
export const zOptionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo ${max} caracteres.`)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();

export const zEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email('Correo electrónico inválido.')
  .max(254);

/** Teléfono laxo a propósito: hay inquilinos extranjeros y prefijos internacionales. */
export const zPhone = z
  .string()
  .trim()
  .regex(/^[+]?[\d\s().-]{6,24}$/, 'Teléfono inválido.')
  .transform((value) => value.replace(/\s+/g, ' '));

/** Código postal español: 5 dígitos, y los dos primeros son la provincia (01–52). */
export const zPostalCode = z
  .string()
  .trim()
  .regex(/^\d{5}$/, 'El código postal son 5 dígitos.')
  .refine((value) => {
    const province = Number(value.slice(0, 2));
    return province >= 1 && province <= 52;
  }, 'Ese código postal no corresponde a ninguna provincia española.');

/**
 * Referencia catastral. Es el identificador oficial del inmueble en España y lo que permite
 * no dar de alta el mismo piso dos veces.
 *
 * Admite 14 O 20 caracteres, y la distinción importa: los 14 primeros identifican la PARCELA
 * y los 6 últimos el inmueble concreto dentro de ella. CartoCiudad, al geocodificar un portal,
 * devuelve la de parcela (14), porque un portal no es un piso; exigir 20 hacía que nada de lo
 * que devuelve la fuente oficial pasara la validación.
 */
/**
 * Referencia catastral: texto libre. Lo normal son 20 caracteres (inmueble) o 14 (parcela),
 * pero no se impone formato: se guarda lo que traiga la escritura o el recibo del IBI.
 */
export const zCadastralReference = z
  .string()
  .trim()
  .toUpperCase()
  .min(1, 'Escribe la referencia catastral o deja el campo vacío.')
  .max(50, 'Como mucho 50 caracteres.');

/** Latitud/longitud con la precisión de `Decimal(9,6)` del esquema. */
export const zLatitude = z.number().min(-90).max(90);
export const zLongitude = z.number().min(-180).max(180);

/** Porcentaje de indexación: de −100 % a +100 %, con 3 decimales (el IPC es 2,1 %). */
export const zPercent = z.number().min(-100).max(100);

/** Día del mes en que vence la renta. 1–28 a propósito: el 30 no existe en febrero. */
export const zPaymentDay = z
  .number()
  .int()
  .min(1, 'El día de pago debe estar entre 1 y 28.')
  .max(28, 'Usa un día entre 1 y 28: los días 29-31 no existen en todos los meses.');

/**
 * Página y límite de los listados. Coincide con `apps/api/src/lib/pagination.ts`, que sigue
 * siendo la fuente para las rutas de solo lectura de la fase 1.
 */
export const zPagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
});

/**
 * Convierte un `ZodError` al formato `details[]` que devuelve la API y que el cliente usa para
 * pintar el mensaje EN EL CAMPO correcto.
 */
export function issuesOf(error: z.ZodError): { path: string; message: string }[] {
  return error.issues.map((issue) => ({
    path: issue.path.join('.'),
    message: issue.message,
  }));
}
